import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { platformAPI, fileUrl } from '../../services/api';
import {
  LayoutDashboard, Building2, CreditCard, FileText, Users,
  HelpCircle, Bell, Settings, Activity, LogOut, ChevronLeft,
  ChevronRight, Search, Shield, BarChart3, Menu, X, TrendingUp, MessageSquare, Globe,
} from 'lucide-react';
import './superAdmin.css';

const NAV_ITEMS = [
  { to: '/super-admin', icon: LayoutDashboard, label: 'Overview', exact: true },
  { to: '/super-admin/companies', icon: Building2, label: 'Companies' },
  { to: '/super-admin/subscriptions', icon: CreditCard, label: 'Subscriptions' },
  { to: '/super-admin/plans', icon: FileText, label: 'Plans' },
  { to: '/super-admin/payments', icon: BarChart3, label: 'Payments' },
  { to: '/super-admin/reports', icon: TrendingUp, label: 'Reports' },
  { to: '/super-admin/users', icon: Users, label: 'Users' },
  { type: 'divider' },
  { to: '/super-admin/notifications', icon: Bell, label: 'Notifications' },
  { to: '/super-admin/activity', icon: Activity, label: 'Activity Logs' },
  { to: '/super-admin/support', icon: HelpCircle, label: 'Support' },
  { to: '/super-admin/chat', icon: MessageSquare, label: 'Live Chat' },
  { to: '/super-admin/legal', icon: FileText, label: 'Legal & Compliance' },
  { to: '/super-admin/landing', icon: Globe, label: 'Landing Page' },
  { to: '/super-admin/settings', icon: Settings, label: 'Settings' },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoPath, setLogoPath] = useState(null);

  useEffect(() => {
    platformAPI.getNotifications().then(data => {
      setUnreadCount(data.filter(n => !n.is_read).length);
    }).catch(() => {});
    platformAPI.getSettings().then(data => {
      if (data.logo_path) setLogoPath(data.logo_path);
    }).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/super-admin/login');
  };

  return (
    <div className="sa-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="sa-mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sa-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sa-sidebar-header">
          {!collapsed && (
            <div className="sa-sidebar-brand">
              {logoPath ? (
                <img src={fileUrl(logoPath)} alt="Logo" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
              ) : (
                <Shield size={20} className="sa-brand-icon" />
              )}
              <div>
                <div className="sa-brand-name">Ledger Pro</div>
                <div className="sa-brand-sub">Platform Administration</div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="sa-sidebar-brand-collapsed">
              <Shield size={20} className="sa-brand-icon" />
            </div>
          )}
          <button className="sa-collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <nav className="sa-sidebar-nav">
          {NAV_ITEMS.map((item, i) => {
            if (item.type === 'divider') {
              return <div key={i} className="sa-nav-divider" />;
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) => `sa-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={18} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="sa-sidebar-footer">
          <button className="sa-nav-item sa-logout-btn" onClick={handleLogout} title={collapsed ? 'Sign Out' : undefined}>
            <LogOut size={18} />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="sa-main">
        {/* Top bar */}
        <header className="sa-topbar">
          <div className="sa-topbar-left">
            <button className="sa-mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="sa-page-title">
              {NAV_ITEMS.find(n => n.to && location.pathname === n.to)?.label ||
               NAV_ITEMS.find(n => n.to && location.pathname.startsWith(n.to) && n.to !== '/super-admin')?.label ||
               'Platform Overview'}
            </h1>
          </div>
          <div className="sa-topbar-right">
            <div className="sa-search-box">
              <Search size={14} />
              <input placeholder="Search platform..." />
            </div>
            <button className="sa-topbar-icon-btn" onClick={() => navigate('/super-admin/notifications')}>
              <Bell size={18} />
              {unreadCount > 0 && <span className="sa-badge">{unreadCount}</span>}
            </button>
            <div className="sa-admin-avatar">
              <div className="sa-avatar-circle">
                {user?.username?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="sa-avatar-info">
                <span className="sa-avatar-name">{user?.full_name || user?.username}</span>
                <span className="sa-avatar-role">Super Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="sa-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
